import React, { createContext, useContext, useState } from 'react';
// Removed old accountsData import - actions now come from dynamic account data

const ActionsContext = createContext();

// Mock team members - in production, this would come from your backend
const TEAM_MEMBERS = [
  { id: 'george', name: 'George Michalke', avatar: 'GM' },
  { id: 'sarah', name: 'Sarah Chen', avatar: 'SC' },
  { id: 'mike', name: 'Mike Johnson', avatar: 'MJ' },
  { id: 'emily', name: 'Emily Davis', avatar: 'ED' },
];

// Sample actions data with specific customer correlations
const INITIAL_ACTIONS = [
  {
    id: 'action-001',
    text: 'Schedule data quality review meeting with TechCorp Solutions',
    accountName: 'TechCorp Solutions',
    status: 'In Progress',
    priority: 'High',
    dueDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
    assignee: 'george',
    completed: false,
    section: 'Top Priorities'
  },
  {
    id: 'action-002',
    text: 'Deliver API integration documentation to Global Retail Inc',
    accountName: 'Global Retail Inc',
    status: 'Not Started',
    priority: 'Medium',
    dueDate: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString(),
    assignee: 'sarah',
    completed: false,
    section: 'Top Priorities'
  },
  {
    id: 'action-003',
    text: 'Review quarterly performance metrics for HealthFirst Medical',
    accountName: 'HealthFirst Medical',
    status: 'Complete',
    priority: 'Low',
    dueDate: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
    assignee: 'mike',
    completed: true,
    section: 'Recently Assigned'
  },
  {
    id: 'action-004',
    text: 'Implement custom validation rules for EduLearn Systems',
    accountName: 'EduLearn Systems',
    status: 'In Progress',
    priority: 'Critical',
    dueDate: new Date(Date.now() + 3 * 24 * 60 * 60 * 1000).toISOString(),
    assignee: 'emily',
    completed: false,
    section: 'In Progress'
  },
  {
    id: 'action-005',
    text: 'Resolve authentication issues for FinanceHub Pro',
    accountName: 'FinanceHub Pro',
    status: 'On Hold',
    priority: 'High',
    dueDate: new Date(Date.now() + 5 * 24 * 60 * 60 * 1000).toISOString(),
    assignee: 'george',
    completed: false,
    section: 'Top Priorities'
  },
  {
    id: 'action-006',
    text: 'Conduct training session for CloudScale Networks team',
    accountName: 'CloudScale Networks',
    status: 'Not Started',
    priority: 'Medium',
    dueDate: new Date(Date.now() + 10 * 24 * 60 * 60 * 1000).toISOString(),
    assignee: 'sarah',
    completed: false,
    section: 'Low but Important'
  },
  {
    id: 'action-007',
    text: 'Optimize data processing pipeline for DataDrive Analytics',
    accountName: 'DataDrive Analytics',
    status: 'In Progress',
    priority: 'High',
    dueDate: new Date(Date.now() + 8 * 24 * 60 * 60 * 1000).toISOString(),
    assignee: 'mike',
    completed: false,
    section: 'In Progress'
  },
  {
    id: 'action-008',
    text: 'Set up security audit for SecureOps Platform',
    accountName: 'SecureOps Platform',
    status: 'Complete',
    priority: 'Critical',
    dueDate: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(),
    assignee: 'emily',
    completed: true,
    section: 'Recently Assigned'
  },
  {
    id: 'action-009',
    text: 'Review contract renewal terms with MarketEdge Trading',
    accountName: 'MarketEdge Trading',
    status: 'Not Started',
    priority: 'Low',
    dueDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
    assignee: null,
    completed: false,
    section: 'Low but Important'
  },
  {
    id: 'action-010',
    text: 'Deploy new monitoring dashboard for LogiFlow Transport',
    accountName: 'LogiFlow Transport',
    status: 'In Progress',
    priority: 'Medium',
    dueDate: new Date(Date.now() + 6 * 24 * 60 * 60 * 1000).toISOString(),
    assignee: 'george',
    completed: false,
    section: 'In Progress'
  },
  {
    id: 'action-011',
    text: 'Troubleshoot data sync issues for GreenEnergy Solar',
    accountName: 'GreenEnergy Solar',
    status: 'Complete',
    priority: 'High',
    dueDate: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString(),
    assignee: 'sarah',
    completed: true,
    section: 'Recently Assigned'
  },
  {
    id: 'action-012',
    text: 'Create custom reports for SmartHome IoT',
    accountName: 'SmartHome IoT',
    status: 'Not Started',
    priority: 'Low',
    dueDate: new Date(Date.now() + 20 * 24 * 60 * 60 * 1000).toISOString(),
    assignee: 'mike',
    completed: false,
    section: 'Low but Important'
  }
];

export const ActionsProvider = ({ children }) => {
  // Initialize state directly with the sample actions
  const [actions, setActions] = useState(INITIAL_ACTIONS);

  const toggleActionComplete = (actionId) => {
    setActions(prevActions => 
      prevActions.map(action => {
        if (action.id === actionId) {
          const newCompleted = !action.completed;
          return { 
            ...action, 
            completed: newCompleted,
            status: newCompleted ? 'Complete' : 'Not Started'
          };
        }
        return action;
      })
    );
  };

  const assignAction = (actionId, assigneeId) => {
    setActions(prevActions => 
      prevActions.map(action => 
        action.id === actionId 
          ? { ...action, assignee: assigneeId }
          : action
      )
    );
  };

  const updateActionDueDate = (actionId, dueDate) => {
    setActions(prevActions => 
      prevActions.map(action => 
        action.id === actionId 
          ? { ...action, dueDate }
          : action
      )
    );
  };

  const updateActionStatus = (actionId, status) => {
    setActions(prevActions => 
      prevActions.map(action => {
        if (action.id === actionId) {
          // Sync completed state with status
          const completed = status === 'Complete';
          return { ...action, status, completed };
        }
        return action;
      })
    );
  };

  const updateActionPriority = (actionId, priority) => {
    setActions(prevActions => 
      prevActions.map(action => 
        action.id === actionId 
          ? { ...action, priority }
          : action
      )
    );
  };

  const deleteAction = (actionId) => {
    setActions(prevActions => prevActions.filter(action => action.id !== actionId));
  };

  const value = {
    actions,
    teamMembers: TEAM_MEMBERS,
    toggleActionComplete,
    assignAction,
    updateActionDueDate,
    updateActionStatus,
    updateActionPriority,
    deleteAction,
  };

  return (
    <ActionsContext.Provider value={value}>
      {children}
    </ActionsContext.Provider>
  );
};

export const useActions = () => {
  const context = useContext(ActionsContext);
  if (!context) {
    throw new Error('useActions must be used within an ActionsProvider');
  }
  return context;
};